<script setup lang="ts">
import { ref, computed } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useModal } from '@/composables/useModalStack'
import { useI18n } from '@/i18n'
import { exportImage, renderExportBlob } from '@/app/actions'
import { canEncode, type ExportFormat } from '@/io/imageEncode'
import { onBeforeUnmount, watch } from 'vue'

const emit = defineEmits<{ close: [] }>()
useModal('export')
const { t } = useI18n()
const docStore = useDocumentStore()
const format = ref<ExportFormat>('png')
const quality = ref(92)
const scale = ref(100)
const busy = ref(false)
const outW = computed(() => Math.max(1, Math.round((docStore.doc?.meta.width ?? 0) * scale.value / 100)))
const outH = computed(() => Math.max(1, Math.round((docStore.doc?.meta.height ?? 0) * scale.value / 100)))
const avifOk = ref(false)
const webpOk = ref(true)
void canEncode('avif').then(v => (avifOk.value = v))
void canEncode('webp').then(v => (webpOk.value = v))
const estimate = ref<number | null>(null)
const previewUrl = ref<string | null>(null)
let timer: number | null = null
let seq = 0
function scheduleEstimate() {
  if (timer !== null) clearTimeout(timer)
  timer = window.setTimeout(async () => {
    timer = null
    const my = ++seq
    try {
      const r = await renderExportBlob({ format: format.value, quality: quality.value / 100, scale: scale.value / 100 })
      if (my !== seq || !r) return
      estimate.value = r.blob.size
      if (previewUrl.value) URL.revokeObjectURL(previewUrl.value)
      previewUrl.value = URL.createObjectURL(r.blob)
    } catch {
      estimate.value = null
    }
  }, 200)
}
watch([format, quality, scale], scheduleEstimate, { immediate: true })
onBeforeUnmount(() => {
  if (timer !== null) clearTimeout(timer)
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value)
})
function fmtBytes(n: number) {
  return n >= 1048576 ? `${(n / 1048576).toFixed(2)} MB` : n >= 1024 ? `${(n / 1024).toFixed(1)} KB` : `${n} B`
}

async function run() {
  busy.value = true
  try {
    await exportImage({ format: format.value, quality: quality.value / 100, scale: scale.value / 100 })
    emit('close')
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="modal-backdrop" @pointerdown.self="emit('close')">
    <div class="modal" @keydown.esc="emit('close')">
      <h2>{{ t('dialog.export.title') }}</h2>
      <label class="field"><span>{{ t('dialog.export.format') }}</span>
        <select v-model="format">
          <option value="png">PNG</option>
          <option value="jpeg">JPEG</option>
          <option v-if="webpOk" value="webp">WebP</option>
          <option v-if="avifOk" value="avif">AVIF</option>
        </select>
      </label>
      <label v-if="format !== 'png'" class="field"><span>{{ t('dialog.export.quality') }}</span>
        <span class="row"><input v-model.number="quality" type="range" min="1" max="100" /><span class="mono">{{ quality }}</span></span>
      </label>
      <label class="field"><span>{{ t('dialog.export.scale') }}</span>
        <span class="row"><input v-model.number="scale" type="number" min="1" max="400" /> %</span>
      </label>
      <div class="field"><span>{{ t('dialog.export.size') }}</span><span class="mono">{{ outW }} × {{ outH }} px</span></div>
      <div class="field"><span>{{ t('export.estimated') }}</span><span class="mono">{{ estimate === null ? '…' : fmtBytes(estimate) }}</span></div>
      <div class="preview">
        <span class="muted">{{ t('export.preview') }}</span>
        <div class="pv"><img v-if="previewUrl" :src="previewUrl" alt="" /></div>
      </div>
      <p class="muted small">{{ t('export.stripMeta') }}</p>
      <p v-if="format === 'jpeg'" class="muted">{{ t('dialog.export.transparentHint') }}</p>
      <div class="actions">
        <button @click="emit('close')">{{ t('dialog.cancel') }}</button>
        <button class="primary" :disabled="busy" @click="run">{{ t('dialog.export.run') }}</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.preview {
  margin: 6px 0;
}
.pv {
  margin-top: 4px;
  width: 100%;
  max-height: 260px;
  overflow: auto;
  background: repeating-conic-gradient(var(--checker-a) 0 25%, var(--checker-b) 0 50%) 0 0 / 16px 16px;
  border: 1px solid var(--line-strong);
}
.pv img {
  display: block;
  max-width: 100%;
  image-rendering: pixelated;
}
.small {
  font-size: 10px;
}
</style>
